import { Component, OnChanges, PipeTransform, TemplateRef, input } from '@angular/core';
import { DataTableBodyRowComponent } from '../body-row.component';
import { TableColumn } from '../../../types/table-column.type';

export interface ISummaryColumn {
  summaryFunc?: (cells: Array<unknown>) => unknown;
  summaryTemplate?: TemplateRef<unknown>;

  prop: string;
  pipe?: PipeTransform;
}

function defaultSumFunc(cells: Array<unknown>): unknown {
  const cellsWithValues = cells.filter((cell: unknown) => !!cell);

  if (!cellsWithValues.length) {
    return null;
  }
  if (cellsWithValues.some((cell: unknown) => typeof cell !== 'number')) {
    return null;
  }

  return cellsWithValues.reduce((res: unknown, cell: unknown) => (res as number) + (cell as number));
}

function noopSumFunc(): void {
  return null;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'datatable-summary-row',
  template: `
      @if (summaryRow && _internalColumns) {
        <datatable-body-row [innerWidth]="innerWidth()"
          [offsetX]="offsetX()"
          [columns]="_internalColumns"
          [rowHeight]="rowHeight()"
          [row]="summaryRow"
          [rowIndex]="-1" />
      }
    `,
  host: {
    class: 'datatable-summary-row'
  },
  imports: [DataTableBodyRowComponent]
})
export class DataTableSummaryRowComponent implements OnChanges {
  readonly rows = input<Array<unknown>>(undefined);
  readonly columns = input<Array<TableColumn>>(undefined);

  readonly rowHeight = input<number | 'auto'>(undefined);
  readonly offsetX = input<number>(undefined);
  readonly innerWidth = input<number>(undefined);

  _internalColumns: Array<TableColumn>;
  summaryRow: Record<string, unknown> = {};

  ngOnChanges(): void {
    if (!this.columns() || !this.rows()) {
      return;
    }
    this.updateInternalColumns();
    this.updateValues();
  }

  private updateInternalColumns(): void {
    this._internalColumns = this.columns().map((col: TableColumn) => ({
      ...col,
      cellTemplate: col.summaryTemplate
    }));
  }

  private updateValues(): void {
    this.summaryRow = {};

    this.columns()
      .filter((col: TableColumn) => !col.summaryTemplate)
      .forEach((col: TableColumn) => {
        const cellsFromSingleColumn = this.rows().map((row: unknown) => (row as Record<string, unknown>)[col.prop as string]);
        const sumFunc = this.getSummaryFunction(col);

        this.summaryRow[col.prop as string] = col.pipe
          ? col.pipe.transform(sumFunc(cellsFromSingleColumn))
          : sumFunc(cellsFromSingleColumn);
      });
  }

  private getSummaryFunction(column: TableColumn): (a: Array<unknown>) => unknown {
    if (column.summaryFunc === undefined) {
      return defaultSumFunc;
    } else if (column.summaryFunc === null) {
      return noopSumFunc;
    } else {
      return column.summaryFunc;
    }
  }
}
